
/**
 * TODO: Documentation
 * `Header` - .
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { FIRST_PAGE_COMPONENT } from '@owc-utils/Elements/SelectorPaths';
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import getAccessibilityStackInstance from '@owc-utils/common/AccessibilityFocusStackHelper';
import headerMixin from '@owc-utils/Mixins/Header';
import owcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { lockWindowScroll, unlockWindowScroll } from '@owc-utils/common/LockWindow';

import {
  HeaderComponent,
  HeaderComputed,
  HeaderData,
  HeaderItem,
  HeaderMethods,
} from '@owc-packages/Header/src/Header';
import {
  HEADER_MOBILE_MENUS,
  HEADER_MAIN_NAV_FLYOUT,
  FOCUS_STACK_HEADER_CONTEXT,
  HEADER_JUMP_TO_CONTENT_ID,
} from '@owc-packages/Header/src/Header.configs';

import { mobileBreakpoints, desktopBreakpoints } from '@owc-utils/MediaQueryService/MediaQueryUtils';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import OwcLanguageSwitch from '@owc-packages/Header/src/LanguageSwitch.vue';
import OwcLogo from '@owc-packages/Header/src/HeaderLogo.vue';
import OwcHeaderButton from '@owc-packages/Header/src/HeaderButton.vue';
import OwcHeaderMercedesMe from '@owc-packages/HeaderMercedesMe/src/HeaderMercedesMe.vue';
import OwcHeaderMainNavigation from '@owc-packages/Header/src/HeaderMainNavigation.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { HeaderTopItems } from './Header.enums';
import { Props } from '../../_types/ComponentInitializeDispatcher';
import { updateActiveFlyout, initializeHeaderIntegrations } from '@owc-utils/Seamless/headerIntegrations';
import { MaskBlockEvent } from '@owc-utils/Seamless/headerIntegrations.d';
import { HeaderEvents } from '../../_utils/Seamless/headerEvents.enum';

const globalFocusStack = getAccessibilityStackInstance();

export default (ComponentInitializeDispatcher as HeaderComponent).extend<
  HeaderData,
  HeaderMethods,
  HeaderComputed,
  Props
>({
  name: 'owc-header',
  components: {
    OwcLanguageSwitch,
    OwcHeaderMercedesMe,
    OwcHeaderMainNavigation,
    OwcLogo,
    OwcHeaderButton,
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [headerMixin, owcDataComponent],
  data() {
    return {
      currentMq: 'mq1',
      mobileButtonLabel: '',
      nativePageLanguage: '',
      isAnyFlyoutOpen: false,
      flyoutBlockClose: false,
      firstPageComponentId: HEADER_JUMP_TO_CONTENT_ID,
    };
  },
  created() {
    this.interceptClass = 'owc-header-flyout-intercept';
    this.headerModelId = HeaderTopItems.HEADER_MOBILE_MENU;
  },
  beforeMount() {
    this.currentMq = this.getCurrentMediaQuery();
  },
  watch: {
    isAnyFlyoutOpen(isOpen, previousOpenState) {
      if (isOpen) {
        lockWindowScroll();
      } else {
        unlockWindowScroll();

        if (previousOpenState) {
          this.flyoutBlockClose = false;
        }
      }
    },
  },
  mounted() {
    if (this.onMQChange) {
      this.onMQChange(this.resizeEventHandler as EventListenerOrEventListenerObject);
    }

    this.onHeaderStateChange(state => {
      this.mobileButtonLabel = state.navigationLabels?.mobileMenuButton;
      this.isAnyFlyoutOpen = Boolean(state.flyoutChain?.length);
    });

    this.setHeaderElement(this.$el);
    //FIXME will be done in later stages for this feature
    initializeHeaderIntegrations(this);

    this.firstWebComponent();

    this.$el.addEventListener(
      HeaderEvents.FLYOUT_BLOCK_CLOSE,
      ({ detail: { shouldBlockClose } }: CustomEvent<MaskBlockEvent>) =>
        (this.flyoutBlockClose = shouldBlockClose),
    );
  },
  computed: {
    languageSwitch() {
      return this.componentData?.languageSwitch;
    },
    imprint() {
      return this.componentData?.imprint;
    },
    publishJson() {
      return JSON.stringify(
        Object.assign(
          { languageSwitch: this.languageSwitch, imprint: this.imprint },
          this.componentData?.mainNavigation,
        ),
      );
    },
    mercedesMeData() {
      return JSON.stringify(this.componentData?.mercedesMe);
    },
    visibilityOption() {
      return this.componentData?.visibilitySettings;
    },
    shouldShowMainNavigation() {
      return Boolean(this.visibilityOption?.mainNavigationEnabled);
    },
    shouldShowSearchBar() {
      return Boolean(this.visibilityOption?.searchBarEnabled);
    },
    shouldShowMercedesMe() {
      return Boolean(this.visibilityOption?.mercedesMeEnabled);
    },
    shouldShowB2XUserMenu() {
      return Boolean(this.visibilityOption?.userMenuEnabled);
    },
    shouldShowWishlist() {
      return Boolean(this.visibilityOption?.wishlistEnabled);
    },
    shouldShowLegacyWishlist() {
      return Boolean(this.visibilityOption?.legacyWishlistEnabled);
    },
    shouldShowReachMe() {
      return Boolean(this.visibilityOption?.reachMeEnabled) && !mobileBreakpoints.includes(this.currentMq);
    },
    shouldShowPickerDesktop() {
      return (
        Boolean(this.visibilityOption?.regionPickerEnabled) && desktopBreakpoints.includes(this.currentMq)
      );
    },
    shouldShowPickerMobile() {
      return (
        Boolean(this.visibilityOption?.regionPickerEnabled) && !desktopBreakpoints.includes(this.currentMq)
      );
    },
    homePageLink() {
      return this.componentData?.homePageLink;
    },
    skipToContentLabel() {
      return this.componentData?.skipToContentLabel;
    },
    headerElements() {
      const shouldShowPicker = this.shouldShowPickerMobile;

      return HEADER_MOBILE_MENUS.filter(
        (headerItem: HeaderItem) =>
          !(shouldShowPicker && headerItem.id === HeaderTopItems.HEADER_MODELS_MENU),
      );
    },
  },
  methods: {
    resizeEventHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.currentMq = resizeEvent.detail.current;
    },
    handleHeaderButtonClick(event: Event, buttonId: string) {
      const focusedElement = event.target as HTMLElement;
      this.setModelsFlyoutState(false);

      if (focusedElement) {
        globalFocusStack.pushToFocusStack(focusedElement, FOCUS_STACK_HEADER_CONTEXT);
      }

      const flyoutState = this.getOpenFlyoutId();
      const chain = [{ infoToAdd: HEADER_MAIN_NAV_FLYOUT, flyoutLevel: 1 }];

      updateActiveFlyout(this.$el, HEADER_MAIN_NAV_FLYOUT, Boolean(flyoutState));

      switch (buttonId) {
        case 'modelsMenu': {
          if (flyoutState === buttonId) {
            // repeated clicks close button
            this.closeHeaderFlyout(true);
            this.setOpenFlyoutId(null);
          } else {
            chain.push({ infoToAdd: this.getModelTopicId(), flyoutLevel: 2 });

            // if other flyout is open, close it and open models
            this.closeHeaderFlyout(true);
            this.openHeaderFlyout(chain);
            this.addToTrackingTree(this.getModelTopicId(), 2);
            this.setOpenFlyoutId(buttonId);
            this.setModelsFlyoutState(true);
          }
          break;
        }
        default: {
          if (flyoutState === buttonId) {
            // repeated clicks close button
            this.closeHeaderFlyout(true);
            this.setOpenFlyoutId(null);
          } else {
            // if other flyout is open and user clicks on
            // main, close it
            this.closeHeaderFlyout(false);
            this.openHeaderFlyout(chain);
            this.setOpenFlyoutId(buttonId);
          }
          break;
        }
      }
    },
    handleFlyoutMaskClick(event) {
      if (event.target.classList.contains(this.interceptClass) && !this.flyoutBlockClose) {
        this.closeHeaderFlyout(true);
        this.setOpenFlyoutId(null);
        this.isAnyFlyoutOpen = false;
        updateActiveFlyout(null, null, false);
      }
    },
    firstWebComponent() {
      return document.querySelector(FIRST_PAGE_COMPONENT)?.setAttribute('id', this.firstPageComponentId);
    },
  },
});
